import { supabase } from '../lib/supabase';

/**
 * Email Service for sending notifications via Supabase Edge Function
 */
class EmailService {
  /**
   * Send transaction confirmation email
   */
  async sendTransactionConfirmation(userId, transactionData) {
    try {
      const { data: userProfile } = await supabase?.from('user_profiles')?.select('email, first_name, last_name')?.eq('id', userId)?.single();

      if (!userProfile?.email) {
        throw new Error('User email not found');
      }

      // Check email preferences
      const { data: preferences } = await supabase?.from('email_preferences')?.select('transaction_emails')?.eq('user_id', userId)?.single();

      if (preferences && !preferences?.transaction_emails) {
        console.log('Transaction emails disabled for user');
        return { success: false, message: 'Email notifications disabled' };
      }

      const recipientName = `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`?.trim() || 'Valued Customer';

      // Call Edge Function
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'transaction_confirmation',
          recipientEmail: userProfile?.email,
          recipientName: recipientName,
          data: {
            type: transactionData?.type,
            description: transactionData?.description,
            cryptoSymbol: transactionData?.cryptoSymbol,
            cryptoAmount: transactionData?.cryptoAmount,
            usdAmount: transactionData?.usdAmount,
            feeAmount: transactionData?.feeAmount,
            status: transactionData?.status || 'completed'
          }
        }
      });

      if (error) throw error;

      // Log email sent
      await this.logEmail(userId, 'transaction_confirmation', userProfile?.email, 
        `Transaction ${transactionData?.status || 'Completed'} - ${transactionData?.description}`, 
        'sent', data?.resendId);

      return { success: true, data };
    } catch (error) {
      console.error('Failed to send transaction confirmation:', error);
      return { success: false, error: error?.message };
    }
  }

  /**
   * Send price alert email
   */
  async sendPriceAlert(userId, alertData) {
    try {
      const { data: userProfile } = await supabase?.from('user_profiles')?.select('email, first_name, last_name')?.eq('id', userId)?.single();

      if (!userProfile?.email) {
        throw new Error('User email not found');
      }

      // Check email preferences
      const { data: preferences } = await supabase?.from('email_preferences')?.select('price_alert_emails')?.eq('user_id', userId)?.single();

      if (preferences && !preferences?.price_alert_emails) {
        console.log('Price alert emails disabled for user');
        return { success: false, message: 'Email notifications disabled' };
      }

      const recipientName = `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`?.trim() || 'Valued Customer';

      // Call Edge Function
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'price_alert',
          recipientEmail: userProfile?.email,
          recipientName: recipientName,
          data: {
            cryptoSymbol: alertData?.cryptoSymbol,
            cryptoName: alertData?.cryptoName,
            condition: alertData?.condition,
            targetPrice: alertData?.targetPrice,
            currentPrice: alertData?.currentPrice
          }
        }
      });

      if (error) throw error;

      // Log email sent
      await this.logEmail(userId, 'price_alert', userProfile?.email, 
        `Price Alert: ${alertData?.cryptoName} ${alertData?.condition === 'above' ? 'Exceeded' : 'Dropped Below'} $${alertData?.targetPrice}`, 
        'sent', data?.resendId);

      return { success: true, data };
    } catch (error) {
      console.error('Failed to send price alert:', error);
      return { success: false, error: error?.message };
    }
  }

  /**
   * Send account activity email
   */
  async sendAccountActivity(userId, activityData) {
    try {
      const { data: userProfile } = await supabase?.from('user_profiles')?.select('email, first_name, last_name')?.eq('id', userId)?.single();

      if (!userProfile?.email) {
        throw new Error('User email not found');
      }

      // Check email preferences
      const { data: preferences } = await supabase?.from('email_preferences')?.select('account_activity_emails')?.eq('user_id', userId)?.single();

      if (preferences && !preferences?.account_activity_emails) {
        console.log('Account activity emails disabled for user');
        return { success: false, message: 'Email notifications disabled' };
      }

      const recipientName = `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`?.trim() || 'Valued Customer';

      // Call Edge Function
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'account_activity',
          recipientEmail: userProfile?.email,
          recipientName: recipientName,
          data: {
            activityType: activityData?.activityType,
            description: activityData?.description,
            ipAddress: activityData?.ipAddress,
            location: activityData?.location
          }
        }
      });

      if (error) throw error;

      // Log email sent
      await this.logEmail(userId, 'account_activity', userProfile?.email, 
        `Account Activity: ${activityData?.activityType?.replace('_', ' ')}`, 
        'sent', data?.resendId);

      return { success: true, data };
    } catch (error) {
      console.error('Failed to send account activity email:', error);
      return { success: false, error: error?.message };
    }
  }

  /**
   * Send welcome email to new users
   */
  async sendWelcomeEmail(userId, userEmail, userName) {
    try {
      // Call Edge Function
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'welcome',
          recipientEmail: userEmail,
          recipientName: userName || 'Valued Customer',
          data: {}
        }
      });

      if (error) throw error;

      // Log email sent
      await this.logEmail(userId, 'welcome', userEmail, 
        'Welcome to KryptoStash - Your Secure Crypto Platform', 
        'sent', data?.resendId);

      return { success: true, data };
    } catch (error) {
      console.error('Failed to send welcome email:', error);
      return { success: false, error: error?.message };
    }
  }

  /**
   * Log email to database
   */
  async logEmail(userId, emailType, recipientEmail, subject, status, resendId = null) {
    try {
      const { error } = await supabase?.from('email_log')?.insert({
          user_id: userId,
          email_type: emailType,
          recipient_email: recipientEmail,
          subject: subject,
          status: status,
          resend_id: resendId,
          sent_at: status === 'sent' ? new Date()?.toISOString() : null
        });

      if (error) {
        console.error('Failed to log email:', error);
      }
    } catch (error) {
      console.error('Error logging email:', error);
    }
  }
}

export default new EmailService();